import { useParams, Link } from 'react-router';
import { useState, useEffect, useMemo } from 'react';
import { useBusinessId } from '@/hooks/useBusinessId';
import { customersApi, type Customer, type CustomerAppointment } from '@/api/customers';
import { formatDateShort } from '@/utils/date';
import { History, Loader2, Phone, Mail, User } from 'lucide-react';

export function ClientDNA() {
  const { customerId } = useParams();
  const businessId = useBusinessId();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [appointments, setAppointments] = useState<CustomerAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!businessId || !customerId) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [cust, apts] = await Promise.all([
          customersApi.getById(businessId, Number(customerId)),
          customersApi.getAppointments(businessId, Number(customerId)),
        ]);
        setCustomer(cust);
        setAppointments(apts);
      } catch (err) {
        console.error('Error al cargar ADN del cliente:', err);
        setError('No se pudo cargar la informacion del cliente.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [businessId, customerId]);

  // Derived data
  const sortedAppointments = useMemo(() =>
    [...appointments].sort((a, b) => new Date(b.appointmentDate).getTime() - new Date(a.appointmentDate).getTime()),
    [appointments]
  );

  const totalVisits = useMemo(() =>
    appointments.filter(a => a.stateCode?.toUpperCase() === 'COMPLETED').length,
    [appointments]
  );

  const lastVisitDate = useMemo(() => {
    const completed = sortedAppointments.find(a => a.stateCode?.toUpperCase() === 'COMPLETED');
    return completed?.appointmentDate || null;
  }, [sortedAppointments]);

  // Top services by frequency
  const topServices = useMemo(() => {
    const counts: Record<string, number> = {};
    appointments
      .filter(a => a.stateCode?.toUpperCase() === 'COMPLETED')
      .forEach(a => {
        if (a.serviceName) {
          counts[a.serviceName] = (counts[a.serviceName] || 0) + 1;
        }
      });
    return Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([service, count]) => ({ service, count }));
  }, [appointments]);

  // Top staff by frequency
  const topStaff = useMemo(() => {
    const counts: Record<string, number> = {};
    appointments
      .filter(a => a.stateCode?.toUpperCase() === 'COMPLETED')
      .forEach(a => {
        if (a.userName) {
          counts[a.userName] = (counts[a.userName] || 0) + 1;
        }
      });
    return Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([name, count]) => ({ name, count }));
  }, [appointments]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--cream)' }}>
        <div className="flex items-center gap-3" style={{ color: 'var(--muted-fg)', fontSize: '14px' }}>
          <Loader2 size={20} className="animate-spin" />
          <span>Cargando ADN del cliente...</span>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--cream)' }}>
        <div style={{ fontSize: '14px', color: 'var(--muted-fg)' }}>{error || 'Cliente no encontrado'}</div>
      </div>
    );
  }

  const fullName = `${customer.firstName} ${customer.lastName}`.trim();

  const getInitials = (name: string) =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const formatDate = formatDateShort;

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>
      {/* MOBILE LAYOUT (< 768px) */}
      <div className="md:hidden h-full overflow-auto pb-24">
        {/* Compact Client Header */}
        <div style={{ backgroundColor: 'var(--dark-teal)', padding: '16px 16px 14px' }}>
          <div className="flex items-center gap-3">
            <div className="rounded-full flex items-center justify-center flex-shrink-0" style={{ width: '52px', height: '52px', backgroundColor: 'var(--terracotta)' }}>
              <span className="text-white" style={{ fontFamily: 'var(--font-heading)', fontSize: '18px' }}>{getInitials(fullName)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white truncate" style={{ fontFamily: 'var(--font-heading)', fontSize: '17px' }}>{fullName}</div>
              <div className="flex items-center gap-2 mt-0.5">
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>Cliente</span>
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>·</span>
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>{totalVisits} visitas</span>
              </div>
            </div>
            <Link
              to={`/customer/${customerId}/history`}
              className="flex items-center justify-center rounded-lg flex-shrink-0"
              style={{ width: '36px', height: '36px', border: '1px solid rgba(255,255,255,0.15)' }}
            >
              <History size={15} style={{ color: 'rgba(255,255,255,0.7)' }} />
            </Link>
          </div>

          {/* Quick Info Row */}
          <div className="flex items-center gap-3 mt-3 min-w-0" style={{ fontSize: '11px' }}>
            {customer.phone && <span className="flex-shrink-0" style={{ color: 'var(--peach)' }}>{customer.phone}</span>}
            {customer.phone && customer.email && <span className="flex-shrink-0" style={{ color: 'rgba(255,255,255,0.25)' }}>·</span>}
            {customer.email && <span className="truncate min-w-0" style={{ color: 'rgba(255,255,255,0.6)' }}>{customer.email}</span>}
          </div>
        </div>

        {/* Mobile Content */}
        <div style={{ padding: '12px 14px 24px' }}>
          {/* Recent Appointments */}
          <div className="rounded-xl" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '12px 14px', marginBottom: '10px' }}>
            <div className="section-label" style={{ marginBottom: '8px' }}>Citas Recientes</div>
            {sortedAppointments.length === 0 ? (
              <div style={{ fontSize: '13px', color: 'var(--muted-fg)' }}>Sin historial</div>
            ) : (
              <div className="space-y-0">
                {sortedAppointments.slice(0, 4).map((apt, i) => (
                  <div
                    key={apt.id}
                    style={{ padding: '5px 0', borderBottom: i < Math.min(sortedAppointments.length, 4) - 1 ? '1px solid var(--border-color)' : 'none' }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate min-w-0" style={{ fontSize: '13px', color: 'var(--dark-teal)' }}>{apt.serviceName}</span>
                      <span className="flex-shrink-0" style={{ fontSize: '11px', color: 'var(--muted-fg)' }}>{apt.appointmentDate}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <span className="truncate min-w-0" style={{ fontSize: '11px', color: 'var(--muted-fg)' }}>con {apt.userName}</span>
                      <span className="flex-shrink-0" style={{ fontSize: '11px', color: 'var(--muted-fg)' }}>{apt.stateName}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top Services + Top Staff side by side */}
          <div className="grid grid-cols-2 gap-2.5" style={{ marginBottom: '10px' }}>
            {/* Top Services */}
            <div className="rounded-xl" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '12px 14px' }}>
              <div className="section-label" style={{ marginBottom: '8px' }}>Servicios Top</div>
              {topServices.length === 0 ? (
                <div style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>Sin datos</div>
              ) : (
                <div className="space-y-1.5">
                  {topServices.slice(0, 3).map((fav, i) => (
                    <div key={i} className="flex items-center justify-between gap-2">
                      <span className="truncate min-w-0" style={{ fontSize: '12px', color: 'var(--dark-teal)' }}>{fav.service}</span>
                      <span className="flex-shrink-0" style={{ fontSize: '11px', fontWeight: 500, color: 'var(--terracotta)', backgroundColor: 'rgba(224, 123, 84, 0.08)', padding: '0px 6px', borderRadius: '999px' }}>{fav.count}x</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top Staff */}
            <div className="rounded-xl" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '12px 14px' }}>
              <div className="section-label" style={{ marginBottom: '8px' }}>Profesionales</div>
              {topStaff.length === 0 ? (
                <div style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>Sin datos</div>
              ) : (
                <div className="space-y-1.5">
                  {topStaff.map((s, i) => (
                    <div key={i} className="flex items-center justify-between gap-2">
                      <span className="truncate min-w-0" style={{ fontSize: '12px', color: 'var(--dark-teal)' }}>{s.name}</span>
                      <span className="flex-shrink-0" style={{ fontSize: '11px', fontWeight: 500, color: 'var(--terracotta)', backgroundColor: 'rgba(224, 123, 84, 0.08)', padding: '0px 6px', borderRadius: '999px' }}>{s.count}x</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {customer.notes && (
            <div className="rounded-xl" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '12px 14px', marginBottom: '10px' }}>
              <div className="section-label" style={{ marginBottom: '8px' }}>Notas</div>
              <div style={{ fontSize: '13px', color: 'var(--dark-teal)', lineHeight: 1.5 }}>{customer.notes}</div>
            </div>
          )}
        </div>
      </div>

      {/* DESKTOP/TABLET LAYOUT (>= 768px) */}
      <div className="hidden md:flex flex-1 overflow-hidden">

        {/* LEFT -- Client Identity */}
        <div
          className="flex flex-col overflow-auto"
          style={{ width: '300px', minWidth: '300px', backgroundColor: 'var(--dark-teal)', padding: '24px 20px 16px' }}
        >
          <div className="flex flex-col items-center mb-4">
            <div className="rounded-full flex items-center justify-center" style={{ width: '88px', height: '88px', backgroundColor: 'var(--terracotta)' }}>
              <span className="text-white" style={{ fontFamily: 'var(--font-heading)', fontSize: '28px' }}>{getInitials(fullName)}</span>
            </div>
          </div>

          <div className="text-center mb-5">
            <div className="text-white mb-1" style={{ fontFamily: 'var(--font-heading)', fontSize: '20px' }}>{fullName}</div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>
              Cliente
            </div>
          </div>

          {/* Contact + Key Info */}
          <div className="rounded-lg mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.06)', padding: '10px 12px' }}>
            <div className="space-y-2">
              {customer.phone && (
                <div className="flex justify-between" style={{ fontSize: '12px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.45)' }}>Telefono</span>
                  <span style={{ color: 'var(--peach)' }}>{customer.phone}</span>
                </div>
              )}
              {customer.email && (
                <div className="flex justify-between" style={{ fontSize: '12px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.45)' }}>Email</span>
                  <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '11px' }}>{customer.email}</span>
                </div>
              )}
              <div className="flex justify-between" style={{ fontSize: '12px' }}>
                <span style={{ color: 'rgba(255,255,255,0.45)' }}>Total visitas</span>
                <span style={{ color: 'rgba(255,255,255,0.8)' }}>{totalVisits}</span>
              </div>
              {lastVisitDate && (
                <div className="flex justify-between" style={{ fontSize: '12px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.45)' }}>Ultima visita</span>
                  <span style={{ color: 'rgba(255,255,255,0.8)' }}>{formatDate(lastVisitDate)}</span>
                </div>
              )}
              {topStaff.length > 0 && (
                <div className="flex justify-between" style={{ fontSize: '12px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.45)' }}>Profesional favorito</span>
                  <span style={{ color: 'rgba(255,255,255,0.8)' }}>{topStaff[0].name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {customer.notes && (
            <div className="rounded-lg mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.06)', padding: '10px 12px' }}>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px', fontWeight: 500 }}>Notas</div>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.4 }}>{customer.notes}</div>
            </div>
          )}

          {/* Nav */}
          <div className="mt-auto">
            <Link
              to={`/customer/${customerId}/history`}
              className="flex items-center justify-center gap-1.5 rounded-lg transition-colors hover:bg-white/10"
              style={{ height: '36px', border: '1px solid rgba(255,255,255,0.15)', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}
            >
              <History size={13} /> Ver Todo el Historial
            </Link>
          </div>
        </div>

        {/* RIGHT -- DNA Details */}
        <div className="flex-1 overflow-auto" style={{ padding: '20px 24px 24px' }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '22px', color: 'var(--dark-teal)', marginBottom: '16px' }}>
            ADN del Cliente
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Contact Info */}
            <div className="rounded-xl" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '14px 18px' }}>
              <div className="section-label" style={{ marginBottom: '10px' }}>Informacion de Contacto</div>
              <div className="space-y-2.5">
                <div className="flex items-center gap-2" style={{ fontSize: '13px' }}>
                  <User size={14} style={{ color: 'var(--muted-fg)' }} />
                  <span style={{ color: 'var(--dark-teal)' }}>{fullName}</span>
                </div>
                {customer.phone && (
                  <div className="flex items-center gap-2" style={{ fontSize: '13px' }}>
                    <Phone size={14} style={{ color: 'var(--muted-fg)' }} />
                    <span style={{ color: 'var(--dark-teal)' }}>{customer.phone}</span>
                  </div>
                )}
                {customer.email && (
                  <div className="flex items-center gap-2 min-w-0" style={{ fontSize: '13px' }}>
                    <Mail size={14} style={{ color: 'var(--muted-fg)', flexShrink: 0 }} />
                    <span className="truncate min-w-0" style={{ color: 'var(--dark-teal)' }}>{customer.email}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Visit Summary */}
            <div className="rounded-xl" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '14px 18px' }}>
              <div className="section-label" style={{ marginBottom: '10px' }}>Resumen de Visitas</div>
              <div className="space-y-2.5">
                <div className="flex justify-between" style={{ fontSize: '13px' }}>
                  <span style={{ color: 'var(--muted-fg)' }}>Total completadas</span>
                  <span style={{ color: 'var(--dark-teal)', fontWeight: 600 }}>{totalVisits}</span>
                </div>
                <div className="flex justify-between" style={{ fontSize: '13px' }}>
                  <span style={{ color: 'var(--muted-fg)' }}>Total citas</span>
                  <span style={{ color: 'var(--dark-teal)' }}>{appointments.length}</span>
                </div>
                {lastVisitDate && (
                  <div className="flex justify-between" style={{ fontSize: '13px' }}>
                    <span style={{ color: 'var(--muted-fg)' }}>Ultima visita</span>
                    <span style={{ color: 'var(--dark-teal)' }}>{formatDate(lastVisitDate)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Top Services */}
            <div className="rounded-xl" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '14px 18px' }}>
              <div className="section-label" style={{ marginBottom: '10px' }}>Servicios Favoritos</div>
              {topServices.length === 0 ? (
                <div style={{ fontSize: '13px', color: 'var(--muted-fg)' }}>Sin datos suficientes</div>
              ) : (
                <div className="space-y-2">
                  {topServices.map((fav, i) => (
                    <div key={i} className="flex items-center justify-between gap-2">
                      <span className="truncate min-w-0" style={{ fontSize: '13px', color: 'var(--dark-teal)' }}>{fav.service}</span>
                      <span
                        className="flex-shrink-0"
                        style={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: 'var(--terracotta)',
                          backgroundColor: 'rgba(224, 123, 84, 0.08)',
                          padding: '1px 8px',
                          borderRadius: '999px'
                        }}
                      >
                        {fav.count}x
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top Staff */}
            <div className="rounded-xl" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '14px 18px' }}>
              <div className="section-label" style={{ marginBottom: '10px' }}>Profesionales Frecuentes</div>
              {topStaff.length === 0 ? (
                <div style={{ fontSize: '13px', color: 'var(--muted-fg)' }}>Sin datos suficientes</div>
              ) : (
                <div className="space-y-2">
                  {topStaff.map((s, i) => (
                    <div key={i} className="flex items-center justify-between gap-2">
                      <span className="truncate min-w-0" style={{ fontSize: '13px', color: 'var(--dark-teal)' }}>{s.name}</span>
                      <span
                        className="flex-shrink-0"
                        style={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: 'var(--terracotta)',
                          backgroundColor: 'rgba(224, 123, 84, 0.08)',
                          padding: '1px 8px',
                          borderRadius: '999px'
                        }}
                      >
                        {s.count}x
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="rounded-xl col-span-2" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '14px 18px' }}>
              <div className="section-label" style={{ marginBottom: '10px' }}>Notas del Cliente</div>
              {customer.notes ? (
                <div style={{ fontSize: '13px', color: 'var(--dark-teal)', lineHeight: 1.55 }}>{customer.notes}</div>
              ) : (
                <div style={{ fontSize: '13px', color: 'var(--muted-fg)' }}>Sin notas registradas</div>
              )}
            </div>
          </div>

          {/* Recent Appointment History */}
          <div className="rounded-xl mt-4" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', padding: '14px 18px' }}>
            <div className="section-label" style={{ marginBottom: '10px' }}>Citas Recientes (Todos los Profesionales)</div>
            {sortedAppointments.length === 0 ? (
              <div style={{ fontSize: '13px', color: 'var(--muted-fg)' }}>Sin historial</div>
            ) : (
              <div className="space-y-0">
                {sortedAppointments.slice(0, 6).map((apt, i) => (
                  <div
                    key={apt.id}
                    className="flex items-center justify-between"
                    style={{ padding: '6px 0', borderBottom: i < Math.min(sortedAppointments.length, 6) - 1 ? '1px solid var(--border-color)' : 'none' }}
                  >
                    <div className="flex items-center gap-4">
                      <span style={{ fontSize: '12px', color: 'var(--muted-fg)', width: '80px' }}>{apt.appointmentDate}</span>
                      <span style={{ fontSize: '13px', color: 'var(--dark-teal)' }}>{apt.serviceName}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>{apt.userName}</span>
                      <span style={{
                        fontSize: '11px',
                        color: apt.stateCode?.toUpperCase() === 'COMPLETED' ? 'var(--dark-teal)' : 'var(--muted-fg)',
                        backgroundColor: apt.stateCode?.toUpperCase() === 'COMPLETED' ? 'rgba(36, 62, 61, 0.08)' : 'rgba(0,0,0,0.04)',
                        padding: '1px 6px',
                        borderRadius: '999px'
                      }}>{apt.stateName}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
